<script lang="ts" setup>
import type { DataColumn, DataListStyling } from '~/components/TheDataList.vue'
import type { RouterOutput } from '~/types'

type AccountPerson = Omit<Exclude<RouterOutput['persons']['findOnePerson'], null>, 'account' | 'legalRepresentative'>
defineProps<{
  person: AccountPerson
  styling?: DataListStyling
  hiddenKeys?: Array<keyof AccountPerson>
}>()

const { $i18n } = useNuxtApp()
const { isCityAccount } = useAuthorization()
const columns: DataColumn<AccountPerson>[] = [
  {
    key: 'firstName',
    title: $i18n.t('account.name.label'),
    render: ({ firstName, lastName }) => `${firstName} ${lastName}`,
  },
  {
    key: 'birthDate',
    title: $i18n.t('person.birthDate.label'),
    render: ({ birthDate }) => birthDate && $i18n.d(birthDate, 'short'),
  },
  {
    key: 'deathDate',
    isVisible: () => isCityAccount.value,
    title: $i18n.t('person.deathDate.label'),
    render: ({ deathDate }) => deathDate && $i18n.d(deathDate, 'short'),
  },
  {
    key: 'gender',
    title: $i18n.t('person.gender.label'),
    render: ({ gender }) => gender && $i18n.t(`person.gender.${gender}`),
  },
  {
    key: 'isPregnant',
    title: $i18n.t('person.isPregnant.label'),
    render: ({ isPregnant }) => isPregnant && $i18n.t(`person.isPregnant.${isPregnant}`),
  },
  {
    key: 'isSmoker',
    title: $i18n.t('person.isSmoker.label'),
    render: ({ isSmoker }) => isSmoker && $i18n.t(`person.isSmoker.${isSmoker}`),
  },
  {
    key: 'weightInKg',
    title: $i18n.t('person.weight.label'),
  },
  {
    key: 'heightInCm',
    title: $i18n.t('person.height.label'),
  },
  {
    key: 'bmi',
    title: $i18n.t('person.bmi.label'),
  },
  {
    key: 'relevantPredispositions',
    title: $i18n.t('person.relevantPredispositions.label'),
    render: ({ relevantPredispositions }) => {
      if (relevantPredispositions.length === 0) {
        return undefined
      }
      return relevantPredispositions.map(({ name }) => name).join(', ')
    },
  },
  {
    key: 'address',
    title: $i18n.t('person.address.label'),
    renderComponent: ({ address }) => useAddressLabel(address),
    rowProps: {
      class: 'col-span-full border-t pt-3',
    },
  },
  {
    key: 'reportingAddress',
    hideIfNullish: true,
    title: $i18n.t('person.reportingAddress.label'),
    renderComponent: ({ reportingAddress }) => useAddressLabel(reportingAddress),
    rowProps: {
      class: 'col-span-full',
    },
  },
  {
    key: 'livingSituation',
    title: $i18n.t('person.livingSituation.label'),
    rowProps: {
      class: 'col-span-full',
    },
  },
]
</script>

<template>
  <TheDataList
    :config="{ data: person, columns, hiddenKeys }"
    :styling="styling"
  />
</template>
